import moment from 'moment-timezone'
import _ from 'lodash'
import _round from 'lodash.round'
import { formatCurrency, formatDate, formatTime } from '@smarttransit/common'
import { addParamsToLocation } from '@smarttransit/common-client'
import { addAlert, isAuthorized, isOnlyInvestor as isOnlyInvestorFn } from '../../../../utilities/helpers'

import {
  getPayoutReportStatus,
  getPayoutReportVehicleBreakdown,
  requestPayoutReport,
  getTransportationUserTransactionLog, markAsPaidToOwner,
  payOwnerAndCheckUntilComplete,
  deletePayout
} from '../../../../services/transportation-user-transaction-logs-service'

import { getTransportationOwnerMomoAndMoneyAccounts } from '../../../../services/transportation-owner-users-service'
import { getUser } from '../../../../services/users-service'
import { getCachedMoneyAccountTypes } from '../../../../services/st-settings-service'
import { payoutFeeLineLabel, summaryFeesValue, summaryKeepsFeeRate } from './payout-fee-label'

const REPORT_TIMEZONE = 'Africa/Accra'

export default {
  name: 'transportation-accounts-payouts-payout',
  props: {
    signedInUser: Object,
    selectedAccount: Object,
    agencies: Array
  },
  data () {
    return {
      apiInProgress: false,
      dateFrom: null,
      dateTo: null,
      currentPayout: null,
      report: null,
      reportError: '',
      agencyId: 'STRANSITGHA',
      moneyAccountId: null,
      markPaidDialog: false,
      markPaidConfirmation: '',
      markPaidDialogError: '',
      manualPayoutDate: null,
      transportationMoneyAccounts: [],
      markPaidApiInProgress: false,
      deletePayoutDialog: false,
      deletePayoutConfirmation: '',
      deletePayoutDialogError: '',
      deletePayoutApiInProgress: false,
      scheduledJobId: null,
      statusPollTimeoutHandle: null
    }
  },
  mounted: function () {
    this.$nextTick(async () => {
      if (this.$route.query.agencyId) {
        this.agencyId = this.$route.query.agencyId
      }

      if (this.$route.params.payoutId && this.$route.params.payoutId !== '0') {
        this.getCurrentPayout(this.$route.params.payoutId)
      } else {
        if (this.$route.query.dateFrom) {
          this.dateFrom = this.$route.query.dateFrom
        }

        if (this.$route.query.dateFrom && this.$route.query.dateTo) {
          this.dateTo = this.$route.query.dateTo
        }

        if (this.$route.query.moneyAccountId) {
          this.moneyAccountId = this.$route.query.moneyAccountId
        }

        const moneyAccountTypes = await getCachedMoneyAccountTypes()
        let validTransportationOwnerUserIds = null

        if (!isAuthorized(this.$props.signedInUser, 'admin')) {
          validTransportationOwnerUserIds = this.$props.signedInUser.transportationOwnerUsers.filter((o) => (o.transportationOwnerId === this.$props.selectedAccount.id)).map((o) => o.id)
        }

        const { transportationMoneyAccounts, transportationMomoAccounts } = await getTransportationOwnerMomoAndMoneyAccounts({ transportationOwnerId: this.$route.params.accountId, transportationOwnerUserIds: validTransportationOwnerUserIds })
        const moneyAccountLogos = this.$store.getters.getMoneyAccountLogos

        if (transportationMomoAccounts.length || transportationMoneyAccounts.length) {
          this.transportationMoneyAccounts = transportationMomoAccounts.map((o) => {
            return {
              text: o.transportationUserIdentifier.phoneNumber,
              metadata: {
                src: moneyAccountLogos ? moneyAccountLogos[o.moneyAccountTypeId] : null,
                moneyAccountTypeId: o.moneyAccountTypeId,
                moneyAccountTypeCategory: moneyAccountTypes.find((m) => m.typeId === o.moneyAccountTypeId)?.typeCategory
              },
              value: o.id
            }
          })

          this.transportationMoneyAccounts = this.transportationMoneyAccounts.concat(transportationMoneyAccounts.map((o) => {
            return {
              text: o.externalId,
              metadata: {
                icon: this.$store.getters.getMoneyAccountTypes.find((m) => m.value === o.moneyAccountTypeId).metadata.icon,
                moneyAccountTypeId: o.moneyAccountTypeId,
                accountMetadata: o.accountMetadata,
                moneyAccountTypeCategory: moneyAccountTypes.find((m) => m.typeId === o.moneyAccountTypeId)?.typeCategory
              },
              value: o.id
            }
          }))

          if (this.transportationMoneyAccounts.length === 1) {
            this.moneyAccountId = this.transportationMoneyAccounts[0].value
          }

          // generate report based on dates provided
          if (this.dateFrom && this.agencyId) {
            this.getPendingPayoutReport()
          }
        }
      }
    })
  },
  watch: {
    dateFrom (val) {
      this.lastParams = addParamsToLocation({ dateFrom: val }, this.$route, this.lastParams)
    },
    dateTo (val) {
      this.lastParams = addParamsToLocation({ dateTo: val }, this.$route, this.lastParams)
    },
    moneyAccountId (val) {
      this.lastParams = addParamsToLocation({ moneyAccountId: val }, this.$route, this.lastParams)
    }
  },
  computed: {
    isSuperAdmin () {
      return isAuthorized(this.$props.signedInUser, 'superadmin')
    },
    isOnlyInvestor () {
      return isOnlyInvestorFn(this.$props.signedInUser)
    },
    currentSelectedAccount () {
      return this.$props.selectedAccount
    },
    currentAgencies () {
      return this.$props.agencies
    },
    currentMomoAccountLabel () {
      if (this.transportationMoneyAccounts.length && this.moneyAccountId) {
        return this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId).text
      }

      return ''
    },
    currentCategoryType () {
      if (this.transportationMoneyAccounts.length && this.moneyAccountId) {
        return this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId)?.metadata?.moneyAccountTypeCategory || ''
      }

      return ''
    },
    currentReportDateRangeLabel () {
      if (!this.report) {
        return ''
      }

      const reportKeys = Object.keys(this.report)
      if (!reportKeys.length) {
        return ''
      }

      const reportItem = this.report[reportKeys[0]]
      return reportItem && reportItem.reportDateRangeLabel ? reportItem.reportDateRangeLabel : ''
    }
  },
  methods: {
    clearStatusPollTimeout () {
      if (this.statusPollTimeoutHandle) {
        clearTimeout(this.statusPollTimeoutHandle)
        this.statusPollTimeoutHandle = null
      }
    },
    isReportProcessingStatus (status) {
      return status === 'processing' || status === 'pending'
    },
    getSelectedMoneyAccount () {
      return this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId)
    },
    getAgencyRecord (agencyId) {
      return (this.$props.agencies || []).find((agency) => agency.value === agencyId) || null
    },
    formatReportDateRangeLabel ({ dateStart, dateEnd }) {
      if (!dateStart) {
        return ''
      }

      const timezoneLabel = moment(dateStart).tz(REPORT_TIMEZONE).format('z')
      const startLabel = formatDate(dateStart, REPORT_TIMEZONE)
      const endLabel = dateEnd ? formatDate(dateEnd, REPORT_TIMEZONE) : ''
      if (!endLabel || endLabel === startLabel) {
        return `${startLabel} ${timezoneLabel}`
      }

      return `${startLabel} — ${endLabel} ${timezoneLabel}`
    },
    isStransitghaAgency () {
      return String(this.agencyId || '').toLowerCase() === 'stransitgha'
    },
    resolvePayoutDateRange () {
      // The preview and the action (payOwner / markAsPaidToOwner) MUST send the
      // same dateStart / dateEnd, otherwise the backend will load a different
      // set of pending PTLs and the action will compute different fees than
      // what was previewed. For Stransitgha specifically, when only one of
      // dateFrom / dateTo is provided we mirror the other so the range covers
      // a single day (matching the preview's `requestStransitghaPayoutReport`
      // behavior).
      const normalizedStartDate = this.dateFrom ? moment.tz(this.dateFrom, 'Y-MM-DD', REPORT_TIMEZONE).startOf('day') : null
      const normalizedEndDate = this.dateTo ? moment.tz(this.dateTo, 'Y-MM-DD', REPORT_TIMEZONE).endOf('day') : null
      const isStransitgha = this.isStransitghaAgency()
      const resolvedStartDate = normalizedStartDate || (isStransitgha && this.dateTo ? moment.tz(this.dateTo, 'Y-MM-DD', REPORT_TIMEZONE).startOf('day') : null)
      const resolvedEndDate = normalizedEndDate || (isStransitgha && this.dateFrom ? moment.tz(this.dateFrom, 'Y-MM-DD', REPORT_TIMEZONE).endOf('day') : null)

      return {
        dateStart: resolvedStartDate ? resolvedStartDate.valueOf() : null,
        dateEnd: resolvedEndDate ? resolvedEndDate.valueOf() : null
      }
    },
    buildPayoutReportRequestParams () {
      const selectedMoneyAccount = this.getSelectedMoneyAccount()
      const isMobileMoney = selectedMoneyAccount && selectedMoneyAccount.metadata && selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'mobile_money'
      const isBank = selectedMoneyAccount && selectedMoneyAccount.metadata && selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'bank'
      const dateRange = this.resolvePayoutDateRange()

      return {
        transportationUserMomoAccountId: isMobileMoney ? this.moneyAccountId : undefined,
        transportationUserMoneyAccountId: isBank ? this.moneyAccountId : undefined,
        dateStart: !_.isNil(dateRange.dateStart) ? dateRange.dateStart : undefined,
        dateEnd: !_.isNil(dateRange.dateEnd) ? dateRange.dateEnd : undefined,
        agencyId: this.agencyId
      }
    },
    async applyStransitghaSummaryReport ({ report, requestParams }) {
      if (!report) {
        this.report = null
        this.reportError = 'Based on the options, no unpaid revenue were found to generate a report'
        return
      }

      this.reportError = ''
      const reportAgencyId = report.agencyId || this.agencyId
      const normalizedReport = Object.assign({}, report, {
        dateStart: requestParams.dateStart,
        dateEnd: requestParams.dateEnd
      })
      this.report = await this.marshallReport({
        [reportAgencyId]: normalizedReport
      })
      await this.fetchStransitghaVehicleBreakdown({ requestParams, reportAgencyId })
    },
    async fetchStransitghaVehicleBreakdown ({ requestParams, reportAgencyId }) {
      const result = await getPayoutReportVehicleBreakdown(requestParams)
      if (!this.report || !this.report[reportAgencyId]) {
        return
      }
      this.report[reportAgencyId].vehicleBreakdown = result && result.vehicleBreakdown ? result.vehicleBreakdown : []
      this.report = await this.marshallReport(this.report)
    },
    pollStransitghaPayoutReportStatus ({ scheduledJobId, requestParams }) {
      this.clearStatusPollTimeout()
      this.statusPollTimeoutHandle = setTimeout(async () => {
        try {
          const statusResult = await getPayoutReportStatus(Object.assign({ scheduledJobId }, requestParams))
          const status = statusResult && statusResult.status
          if (this.isReportProcessingStatus(status)) {
            this.pollStransitghaPayoutReportStatus({ scheduledJobId, requestParams })
            return
          }

          this.scheduledJobId = null
          if (status === 'completed') {
            await this.applyStransitghaSummaryReport({ report: statusResult.report, requestParams })
            this.apiInProgress = false
            return
          }

          if (status === 'failed') {
            this.reportError = statusResult && statusResult.error ? statusResult.error : 'Error in payout report generation'
          } else if (status === 'error') {
            this.reportError = statusResult && statusResult.message ? statusResult.message : 'Source records not yet available. Please try again.'
          } else {
            this.reportError = 'Error in payout report generation'
          }
          this.apiInProgress = false
        } catch (err) {
          this.reportError = `Error in payout report status: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
          this.apiInProgress = false
        }
      }, 4000)
    },
    getCurrentReportItem () {
      if (!this.report) {
        return null
      }

      const reportKeys = Object.keys(this.report)
      return reportKeys.length ? this.report[reportKeys[0]] : null
    },
    getPayoutPaidOutToLabel () {
      if (!this.currentPayout) {
        return ''
      }

      if (this.currentPayout.transportationUserMomoAccount) {
        return this.currentPayout.transportationUserMomoAccount.transportationUserIdentifier.phoneNumber
      }

      if (this.currentPayout.transportationUserMoneyAccount) {
        const accountNumber = this.currentPayout.transportationUserMoneyAccount.externalId
        const accountMetadata = this.currentPayout.transportationUserMoneyAccount.accountMetadata || {}
        const bankName = accountMetadata.bankName
        return [bankName, accountNumber].filter(Boolean).join(' - ')
      }

      return 'Other (external)'
    },
    buildCompletedPayoutPdfFileName () {
      const reportItem = this.getCurrentReportItem()
      const accountLabel = this.currentSelectedAccount && this.currentSelectedAccount.companyName
        ? this.currentSelectedAccount.companyName
        : 'payout'
      const transportationOwnerId = (this.currentSelectedAccount && this.currentSelectedAccount.id) ||
        (this.currentPayout && this.currentPayout.transportationOwnerId) ||
        this.$route.params.accountId ||
        ''
      const dateLabel = reportItem && reportItem.dateUpdatedLabel ? reportItem.dateUpdatedLabel : moment().format('YYYY-MM-DD')
      return (accountLabel + '-' + transportationOwnerId + '-' + dateLabel + '-payout-report')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') + '.pdf'
    },
    toPdfText (value) {
      return String(value === null || value === undefined ? '' : value)
        .replace(/GH\u20b5/g, 'GHS ')
        .replace(/\u2014/g, '-')
        .replace(/\u2013/g, '-')
        .replace(/[^\x20-\x7E]/g, '?')
        .replace(/\s+/g, ' ')
        .trim()
    },
    toPdfTextLines (value) {
      return String(value === null || value === undefined ? '' : value)
        .split(/\r?\n/)
        .map((line) => this.toPdfText(line))
        .filter(function (line) { return !!line })
    },
    escapePdfText (value) {
      return this.toPdfText(value)
        .replace(/\\/g, '\\\\')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)')
    },
    fitPdfText (value, maxLength) {
      const text = this.toPdfText(value)
      if (text.length <= maxLength) {
        return text
      }

      return text.substring(0, maxLength - 3) + '...'
    },
    wrapPdfText (value, maxLength) {
      const text = this.toPdfText(value)
      if (!text) {
        return ['']
      }

      const words = text.split(' ')
      const lines = []
      let currentLine = ''

      words.forEach(function (word) {
        if (!currentLine) {
          currentLine = word
          return
        }

        if ((currentLine + ' ' + word).length <= maxLength) {
          currentLine += ' ' + word
          return
        }

        lines.push(currentLine)
        currentLine = word
      })

      if (currentLine) {
        lines.push(currentLine)
      }

      return lines.length ? lines : ['']
    },
    padPdfText (value, length) {
      const text = this.fitPdfText(value, length)
      if (text.length >= length) {
        return text
      }

      return text + new Array(length - text.length + 1).join(' ')
    },
    estimatePdfTextWidth (value, fontSize) {
      return this.toPdfText(value).length * fontSize * 0.52
    },
    pdfTextCommand ({ text, x, y, fontSize, fontName, color }) {
      const resolvedColor = color || '0.13 0.13 0.13'
      return 'BT /' + (fontName || 'F1') + ' ' + fontSize + ' Tf ' + resolvedColor + ' rg ' + x + ' ' + y + ' Td (' + this.escapePdfText(text) + ') Tj ET'
    },
    pdfTextRightCommand ({ text, rightX, y, fontSize, fontName, color }) {
      const x = Math.max(36, rightX - this.estimatePdfTextWidth(text, fontSize))
      return this.pdfTextCommand({ text: text, x: Math.round(x), y: y, fontSize: fontSize, fontName: fontName, color: color })
    },
    pdfTextCenterCommand ({ text, centerX, y, fontSize, fontName, color }) {
      const x = centerX - (this.estimatePdfTextWidth(text, fontSize) / 2)
      return this.pdfTextCommand({ text: text, x: Math.round(x), y: y, fontSize: fontSize, fontName: fontName, color: color })
    },
    pdfRectCommand ({ x, y, width, height, fillColor, strokeColor }) {
      const commands = []

      if (fillColor) {
        commands.push('q ' + fillColor + ' rg ' + x + ' ' + y + ' ' + width + ' ' + height + ' re f Q')
      }

      if (strokeColor) {
        commands.push('q ' + strokeColor + ' RG ' + x + ' ' + y + ' ' + width + ' ' + height + ' re S Q')
      }

      return commands.join('\n')
    },
    pdfLineCommand ({ x1, y1, x2, y2, strokeColor }) {
      return 'q ' + (strokeColor || '0.88 0.88 0.88') + ' RG ' + x1 + ' ' + y1 + ' m ' + x2 + ' ' + y2 + ' l S Q'
    },
    buildCompletedPayoutPdfContentPages () {
      const reportItem = this.getCurrentReportItem()
      if (!this.currentPayout || !reportItem) {
        throw new Error('No completed payout report is available to download')
      }

      const paidOutTo = this.getPayoutPaidOutToLabel()
      const referenceLines = this.currentPayout.logMetadata && this.currentPayout.logMetadata.notes
        ? this.toPdfTextLines(this.currentPayout.logMetadata.notes)
        : []

      const pages = []
      let commands = []
      let y = 742
      const pageWidth = 612
      const marginX = 36
      const contentWidth = pageWidth - (marginX * 2)
      const labelX = 66
      const valueRightX = 542
      const addPage = () => {
        if (commands.length) {
          pages.push(commands.join('\n'))
        }
        commands = []
        y = 742
      }
      const ensureSpace = (height) => {
        if (y - height < 52) {
          addPage()
        }
      }
      const addText = (text, x, textY, fontSize, fontName, color) => {
        commands.push(this.pdfTextCommand({ text: text, x: x, y: textY, fontSize: fontSize, fontName: fontName, color: color }))
      }
      const addRightText = (text, rightX, textY, fontSize, fontName, color) => {
        commands.push(this.pdfTextRightCommand({ text: text, rightX: rightX, y: textY, fontSize: fontSize, fontName: fontName, color: color }))
      }
      const addCenterText = (text, centerX, textY, fontSize, fontName, color) => {
        commands.push(this.pdfTextCenterCommand({ text: text, centerX: centerX, y: textY, fontSize: fontSize, fontName: fontName, color: color }))
      }
      const addCard = (height) => {
        ensureSpace(height + 24)
        const cardY = y - height
        commands.push(this.pdfRectCommand({
          x: marginX,
          y: cardY,
          width: contentWidth,
          height: height,
          fillColor: '0.985 0.985 0.985',
          strokeColor: '0.88 0.88 0.88'
        }))
        y -= 32
        return cardY
      }
      const addSummaryRow = (label, value, options) => {
        const rowOptions = options || {}
        const valueFontSize = rowOptions.valueSize || 12
        const valueLines = rowOptions.wrapValue
          ? this.wrapPdfText(value, rowOptions.wrapValue)
          : [value]
        addText(label, labelX, y, rowOptions.labelSize || 12, 'F2', rowOptions.labelColor || '0.13 0.13 0.13')
        if (rowOptions.badge) {
          const badgeWidth = Math.max(104, this.estimatePdfTextWidth(value, 11) + 28)
          commands.push(this.pdfRectCommand({
            x: Math.round(valueRightX - badgeWidth),
            y: y - 8,
            width: Math.round(badgeWidth),
            height: 22,
            fillColor: rowOptions.badgeColor || '0.28 0.70 0.32',
            strokeColor: null
          }))
          addRightText(value, valueRightX - 12, y, 11, 'F2', '1 1 1')
        } else {
          valueLines.forEach((line, index) => {
            addRightText(line, valueRightX, y - (index * 13), valueFontSize, rowOptions.valueFont || 'F1', rowOptions.valueColor || '0.13 0.13 0.13')
          })
        }
        y -= rowOptions.rowHeight || Math.max(32, 13 * valueLines.length + 12)
      }
      const addSummaryMultilineRow = (label, values) => {
        const safeValues = values && values.length ? values : ['']
        addText(label, labelX, y, 12, 'F2', '0.13 0.13 0.13')
        safeValues.forEach((value, index) => {
          addRightText(value, valueRightX, y - (index * 13), 10, 'F1', '0.13 0.13 0.13')
        })
        y -= Math.max(32, 13 * safeValues.length + 12)
      }

      addCenterText('Payout Report', pageWidth / 2, y, 20, 'F2', '0.13 0.13 0.13')
      y -= 28
      addCenterText(reportItem.reportDateRangeLabel || '', pageWidth / 2, y, 15, 'F1', '0.35 0.35 0.35')
      y -= 22
      addCenterText('Transaction ID: ' + this.toPdfText(this.currentPayout.id), pageWidth / 2, y, 10, 'F1', '0.13 0.13 0.13')
      y -= 28

      addCard(350 + Math.max(0, referenceLines.length - 1) * 14)
      addText('Payout Summary', labelX, y, 18, 'F2', '0.13 0.13 0.13')
      y -= 40
      addSummaryRow('Agency', reportItem.agencyLabel)
      addSummaryRow('Paid out to', paidOutTo, { wrapValue: 34 })
      if (referenceLines.length) {
        addSummaryMultilineRow('Ref. number/notes', referenceLines)
      }
      addSummaryRow('Gross amount', reportItem.grossPayoutLabel)
      addSummaryRow('Fees', summaryFeesValue(reportItem.feeDeductionLabel, reportItem.feeLabel, reportItem.showSummaryFeeRate))
      addSummaryRow('Amount', reportItem.payoutLabel, { badge: true })
      addSummaryRow('Paid on', reportItem.dateUpdatedLabel, { labelColor: '0.28 0.70 0.32', valueFont: 'F2' })
      addSummaryRow('Purchases for this Period', reportItem.ticketCount)
      if (reportItem.byStUserLabel) {
        addSummaryRow('Marked as paid by', reportItem.byStUserLabel, { rowHeight: 24 })
      }
      y = Math.min(y, 382)

      if (reportItem.vehicleBreakdown && reportItem.vehicleBreakdown.length) {
        const tableRows = reportItem.vehicleBreakdown
        const tableHeight = 74 + ((tableRows.length + 1) * 22)
        addCard(tableHeight)
        addText('Vehicle Breakdown', labelX, y, 16, 'F2', '0.13 0.13 0.13')
        y -= 32
        addText('Vehicle', labelX, y, 10, 'F2', '0.13 0.13 0.13')
        addRightText('Gross Revenue', 330, y, 10, 'F2', '0.13 0.13 0.13')
        addRightText('Fee Deduction', 440, y, 10, 'F2', '0.13 0.13 0.13')
        addRightText('Net Amount', valueRightX, y, 10, 'F2', '0.13 0.13 0.13')
        commands.push(this.pdfLineCommand({ x1: labelX, y1: y - 9, x2: valueRightX, y2: y - 9 }))
        y -= 24

        tableRows.forEach((vehicle) => {
          addText(this.fitPdfText(vehicle.licensePlateNumber, 22), labelX, y, 10, 'F1', '0.13 0.13 0.13')
          addRightText(vehicle.grossAmountLabel, 330, y, 10, 'F1', '0.13 0.13 0.13')
          addRightText(vehicle.feeDeductionLabel, 440, y, 10, 'F1', '0.13 0.13 0.13')
          addRightText(vehicle.netAmountLabel, valueRightX, y, 10, 'F1', '0.13 0.13 0.13')
          y -= 22
        })

        commands.push(this.pdfLineCommand({ x1: labelX, y1: y + 10, x2: valueRightX, y2: y + 10 }))
        addText('Total', labelX, y, 10, 'F2', '0.13 0.13 0.13')
        addRightText(reportItem.vehicleBreakdownTotals.grossAmountLabel, 330, y, 10, 'F2', '0.13 0.13 0.13')
        addRightText(reportItem.vehicleBreakdownTotals.feeDeductionLabel, 440, y, 10, 'F2', '0.13 0.13 0.13')
        addRightText(reportItem.vehicleBreakdownTotals.netAmountLabel, valueRightX, y, 10, 'F2', '0.13 0.13 0.13')
        y -= 38
      }

      if (reportItem.payoutFeesLabels && reportItem.payoutFeesLabels.length) {
        addCard(72 + (reportItem.payoutFeesLabels.length * 26))
        addText('Itemized Fees', labelX, y, 16, 'F2', '0.13 0.13 0.13')
        y -= 36
        reportItem.payoutFeesLabels.forEach((feeItem) => {
          addSummaryRow(feeItem.label, feeItem.valueLabel, { rowHeight: 26 })
        })
      }

      addPage()
      return pages
    },
    buildCompletedPayoutPdfData () {
      const pages = this.buildCompletedPayoutPdfContentPages()

      const objects = []
      const addObject = function (content) {
        objects.push(content)
        return objects.length
      }
      const pageRefs = []
      addObject('<< /Type /Catalog /Pages 2 0 R >>')
      addObject('PAGES_PLACEHOLDER')
      addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>')
      addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>')

      pages.forEach((content) => {
        const contentObjectId = addObject('<< /Length ' + content.length + ' >>\nstream\n' + content + '\nendstream')
        const pageObjectId = addObject('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ' + contentObjectId + ' 0 R >>')
        pageRefs.push(pageObjectId + ' 0 R')
      })

      objects[1] = '<< /Type /Pages /Kids [' + pageRefs.join(' ') + '] /Count ' + pageRefs.length + ' >>'

      let pdf = '%PDF-1.3\n'
      const offsets = [0]
      objects.forEach((objectContent, index) => {
        offsets.push(pdf.length)
        pdf += (index + 1) + ' 0 obj\n' + objectContent + '\nendobj\n'
      })
      const xrefOffset = pdf.length
      pdf += 'xref\n0 ' + (objects.length + 1) + '\n'
      pdf += '0000000000 65535 f \n'
      for (let i = 1; i < offsets.length; i += 1) {
        pdf += String(offsets[i]).padStart(10, '0') + ' 00000 n \n'
      }
      pdf += 'trailer\n<< /Size ' + (objects.length + 1) + ' /Root 1 0 R >>\nstartxref\n' + xrefOffset + '\n%%EOF'

      return pdf
    },
    async downloadCompletedPayoutPdf () {
      return this.buildCompletedPayoutPdfData()
    },
    async getCurrentPayout (id) {
      try {
        this.apiInProgress = true
        this.currentPayout = await getTransportationUserTransactionLog({ id, filter: { include: ['transportationUserMoneyAccount', { transportationUserMomoAccount: 'transportationUserIdentifier' }] } })
        const payoutAgencyId = this.currentPayout.logMetadata.agencyId

        this.report = await this.marshallReport({
          [payoutAgencyId]: {
            agencyId: payoutAgencyId,
            feePercentage: this.currentPayout.logMetadata.feePercentage,
            grossPayout: this.currentPayout.logMetadata.grossPayout,
            feeDeduction: this.currentPayout.logMetadata.feeDeduction,
            payout: this.currentPayout.amount,
            ticketCount: this.currentPayout.logMetadata.ticketCount || this.currentPayout.logMetadata.total,
            currencySymbol: this.currentPayout.logMetadata.currencySymbol,
            currencyCode: this.currentPayout.logMetadata.currencyCode,
            countryCurrencyId: this.currentPayout.countryCurrencyId,
            payoutFees: this.currentPayout.logMetadata.payoutFees || this.currentPayout.logMetadata.payoutFeeSettings,
            dateStart: this.currentPayout.logMetadata.dateStart,
            dateEnd: this.currentPayout.logMetadata.dateEnd,
            excludedDateRanges: this.currentPayout.logMetadata.excludedDateRanges || [],
            byStUserId: this.currentPayout.logMetadata.byStUserId,
            notes: this.currentPayout.logMetadata.notes,
            reportsDataUsed: this.currentPayout.logMetadata.reportsDataUsed === true
          }
        })

        if (String(payoutAgencyId || '').toLowerCase() === 'stransitgha' && this.currentPayout.logMetadata.reportsDataUsed === true) {
          const vehicleBreakdownRequestParams = {
            transportationUserMomoAccountId: this.currentPayout.transportationUserMomoAccountId || undefined,
            transportationUserMoneyAccountId: this.currentPayout.transportationUserMoneyAccountId || undefined,
            dateStart: this.currentPayout.logMetadata.dateStart,
            dateEnd: this.currentPayout.logMetadata.dateEnd,
            agencyId: payoutAgencyId,
            excludeTransportationUserTransactionLogId: this.currentPayout.id
          }

          if (vehicleBreakdownRequestParams.transportationUserMomoAccountId || vehicleBreakdownRequestParams.transportationUserMoneyAccountId) {
            await this.fetchStransitghaVehicleBreakdown({
              requestParams: vehicleBreakdownRequestParams,
              reportAgencyId: payoutAgencyId
            })
          }
        }

        this.apiInProgress = false
      } catch (err) {
        this.apiInProgress = false
      }
    },
    async marshallReport (report) {
      const users = {}

      for (const key in report) {
        if (report[key]) {
          const agency = this.getAgencyRecord(report[key].agencyId)
          report[key].agencyLabel = agency ? agency.text : report[key].agencyId
          report[key].grossPayoutLabel = `${report[key].currencySymbol}${formatCurrency(report[key].grossPayout)}`
          report[key].payoutLabel = `${report[key].currencySymbol}${formatCurrency(report[key].payout)}`
          const feePercentage = !_.isNil(report[key].feePercentage) ? report[key].feePercentage : report[key].fee
          report[key].feePercentage = _.isNil(feePercentage) ? null : Number(feePercentage)
          report[key].feeLabel = _.isNil(report[key].feePercentage) ? 'Fixed fee' : `${_round(report[key].feePercentage * 100, 2)}%`
          report[key].feeDeductionLabel = `${report[key].currencySymbol}${formatCurrency(report[key].feeDeduction)}`
          report[key].ticketCount = !_.isNil(report[key].ticketCount) ? report[key].ticketCount : report[key].total
          report[key].reportDateRangeLabel = this.formatReportDateRangeLabel({
            dateStart: report[key].dateStart,
            dateEnd: report[key].dateEnd
          })
          const payoutFees = report[key].payoutFees || report[key].payoutFeeSettings || null
          report[key].payoutFees = payoutFees

          report[key].payoutFeesLabels = payoutFees
            ? [
              { key: 'smarttransytFee', label: 'SmartTransyt Fee' },
              { key: 'perDayDriverFee', label: 'Per-Day Driver Fee' },
              { key: 'totalKwhCost', legacyKey: 'perKwHFee', label: 'Charging cost' }
            ].filter((item) => {
              const value = _.isNil(payoutFees[item.key]) ? payoutFees[item.legacyKey] : payoutFees[item.key]
              return !_.isNil(value) && Number(value) >= 0
            })
              .map((item) => {
                const value = _.isNil(payoutFees[item.key]) ? payoutFees[item.legacyKey] : payoutFees[item.key]
                return {
                  key: item.key,
                  label: payoutFeeLineLabel(item.key, item.label, report[key].feeLabel),
                  value: Number(value) || 0,
                  valueLabel: `${report[key].currencySymbol}${formatCurrency(Number(value) || 0)}`
                }
              })
            : []
          report[key].showSummaryFeeRate = summaryKeepsFeeRate(report[key].payoutFeesLabels)

          const vehicleBreakdown = Array.isArray(report[key].vehicleBreakdown) ? report[key].vehicleBreakdown : []
          let totalVehicleGrossAmount = 0
          let totalVehicleFeeDeduction = 0
          let totalVehicleNetAmount = 0

          report[key].vehicleBreakdown = vehicleBreakdown.map((item) => {
            const grossAmount = Number(item && item.grossAmount) || 0
            const feeDeduction = Number(item && item.feeDeduction) || 0
            const resolvedNetAmount = Number(item && item.netAmount) || 0
            totalVehicleGrossAmount += grossAmount
            totalVehicleFeeDeduction += feeDeduction
            totalVehicleNetAmount += resolvedNetAmount
            return Object.assign({}, item, {
              grossAmount,
              feeDeduction,
              netAmount: resolvedNetAmount,
              grossAmountLabel: formatCurrency(grossAmount),
              feeDeductionLabel: formatCurrency(feeDeduction),
              netAmountLabel: formatCurrency(resolvedNetAmount)
            })
          })
          report[key].vehicleBreakdownTotals = {
            grossAmount: totalVehicleGrossAmount,
            feeDeduction: totalVehicleFeeDeduction,
            netAmount: totalVehicleNetAmount,
            grossAmountLabel: formatCurrency(totalVehicleGrossAmount, report[key].currencySymbol),
            feeDeductionLabel: formatCurrency(totalVehicleFeeDeduction, report[key].currencySymbol),
            netAmountLabel: formatCurrency(totalVehicleNetAmount, report[key].currencySymbol)
          }

          const excludedDateRanges = Array.isArray(report[key].excludedDateRanges) ? report[key].excludedDateRanges : []

          report[key].excludedDateRangeLabels = excludedDateRanges
            .map((range) => {
              const label = this.formatReportDateRangeLabel({
                dateStart: range && range.dateStart,
                dateEnd: range && range.dateEnd
              })
              return label ? { label, transportationUserTransactionLogId: range && range.transportationUserTransactionLogId } : null
            })
            .filter(Boolean)

          if (this.currentPayout) {
            if (report[key].byStUserId) {
              report[key].dateUpdatedLabel = formatDate(this.currentPayout.dateCreated, REPORT_TIMEZONE)
            } else {
              report[key].dateUpdatedLabel = formatTime(this.currentPayout.dateUpdated, REPORT_TIMEZONE) + ' ' + formatDate(this.currentPayout.dateUpdated, REPORT_TIMEZONE, { displayTimezone: true })
            }
          }

          if (report[key].byStUserId) {
            try {
              const user = users[report[key].byStUserId] || await getUser({ id: report[key].byStUserId, isDeleted: true })
              report[key].byStUserLabel = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
              users[report[key].byStUserId] = user
            } catch (err) {
              addAlert({ message: err, type: 'error' })
              report[key].byStUserLabel = `ID: ${report[key].byStUserId}`
            }
          }

          if (this.currentPayout && report[key].notes) {
            report[key].notesLabel = report[key].notes.replace(/\n/g, '<br>')
          }
        }
      }

      return report
    },
    openMarkPaidDialog () {
      if (this.isOnlyInvestor) {
        return
      }

      this.markPaidConfirmation = ''
      this.manualPayoutDate = null
      this.markPaidDialogError = ''
      this.reportError = ''
      this.markPaidDialog = true
    },
    closeMarkPaidDialog () {
      if (this.markPaidApiInProgress) {
        return
      }

      this.markPaidDialog = false
      this.markPaidConfirmation = ''
      this.manualPayoutDate = null
      this.markPaidDialogError = ''
    },
    openDeletePayoutDialog () {
      this.deletePayoutConfirmation = ''
      this.deletePayoutDialogError = ''
      this.deletePayoutDialog = true
    },
    closeDeletePayoutDialog () {
      if (this.deletePayoutApiInProgress) {
        return
      }

      this.deletePayoutDialog = false
      this.deletePayoutConfirmation = ''
      this.deletePayoutDialogError = ''
    },
    isDeletePayoutConfirmationValid () {
      const confirmation = (this.deletePayoutConfirmation || '').trim()
      const payoutId = this.currentPayout && this.currentPayout.id ? String(this.currentPayout.id).trim() : ''
      return confirmation && (confirmation.toLowerCase() === 'delete' || confirmation === payoutId)
    },
    async deletePayoutAction () {
      if (!this.currentPayout || !this.currentPayout.id) {
        this.deletePayoutDialogError = 'No payout found to delete'
        return
      }

      if (!this.isDeletePayoutConfirmationValid()) {
        this.deletePayoutDialogError = 'Enter "delete" or the payout transaction ID to continue'
        return
      }

      try {
        this.deletePayoutApiInProgress = true
        this.deletePayoutDialogError = ''
        this.reportError = ''
        await deletePayout({ id: this.currentPayout.id })

        addAlert({
          message: 'Successfully deleted payout',
          type: 'success',
          transient: true
        })

        this.deletePayoutDialog = false
        this.$router.push({
          name: 'transportation-accounts-payouts'
        })
      } catch (err) {
        const message = `Error deleting payout: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
        this.deletePayoutDialogError = message
        this.reportError = message
      } finally {
        this.deletePayoutApiInProgress = false
      }
    },
    async markAsPaid () {
      if (this.isOnlyInvestor) {
        return
      }

      const confirmation = (this.markPaidConfirmation || '').trim()

      if (!confirmation) {
        this.markPaidDialogError = 'Please enter a confirmation or transaction ID'
        return
      }

      try {
        this.markPaidApiInProgress = true
        this.markPaidDialogError = ''
        this.reportError = ''
        const selectedMoneyAccount = this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId)

        const dateRange = this.resolvePayoutDateRange()
        const parsedManualPayoutDate = this.manualPayoutDate ? moment(this.manualPayoutDate, 'Y-MM-DD', true) : null

        if (parsedManualPayoutDate && !parsedManualPayoutDate.isValid()) {
          throw new Error('Please enter a valid manual payout date')
        }

        await markAsPaidToOwner({
          transportationUserMomoAccountId: selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'mobile_money' ? this.moneyAccountId : undefined,
          transportationUserMoneyAccountId: selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'bank' ? this.moneyAccountId : undefined,
          dateStart: dateRange.dateStart,
          dateEnd: dateRange.dateEnd,
          agencyId: this.agencyId,
          notes: confirmation === 'paid' ? '' : confirmation,
          dateCreated: parsedManualPayoutDate ? this.manualPayoutDate : undefined
        })

        addAlert({
          message: `Successfully marked as paid`,
          type: 'success',
          transient: true
        })

        this.markPaidDialog = false
        this.$router.push({
          name: 'transportation-accounts-payouts'
        })
      } catch (err) {
        const message = `Error in marking as paid: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
        this.markPaidDialogError = message
        this.reportError = message
      } finally {
        this.markPaidApiInProgress = false
      }
    },
    async requestPayout () {
      if (this.isOnlyInvestor) {
        return
      }

      if (confirm('Please confirm making a payout to: ' + this.currentSelectedAccount.companyName + ' - ' + this.currentMomoAccountLabel)) {
        try {
          this.apiInProgress = true
          const selectedMoneyAccount = this.transportationMoneyAccounts.find(o => o.value === this.moneyAccountId)

          const dateRange = this.resolvePayoutDateRange()
          const confirmedTransaction = await payOwnerAndCheckUntilComplete({
            transportationUserMomoAccountId: selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'mobile_money' && this.moneyAccountId,
            transportationUserMoneyAccountId: selectedMoneyAccount.metadata.moneyAccountTypeCategory === 'bank' && this.moneyAccountId,
            dateStart: dateRange.dateStart,
            dateEnd: dateRange.dateEnd,
            agencyId: this.agencyId
          })

          addAlert({
            message: `Successfully paid owner`,
            type: 'success',
            transient: true
          })

          this.$router.push({
            name: 'transportation-accounts-payouts-payout',
            params: { payoutId: confirmedTransaction.id }
          })
        } catch (err) {
          this.reportError = `Error in payout: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
        } finally {
          this.apiInProgress = false
        }
      }
    },
    async getPendingPayoutReport () {
      if (this.isOnlyInvestor) {
        return
      }

      try {
        this.reportError = ''
        this.report = null
        this.clearStatusPollTimeout()
        this.scheduledJobId = null

        if (this.moneyAccountId && this.agencyId) {
          this.apiInProgress = true
          const requestParams = this.buildPayoutReportRequestParams()
          const requestResult = await requestPayoutReport(requestParams)
          if (requestResult && requestResult.status === 'completed') {
            if (this.isStransitghaAgency()) {
              await this.applyStransitghaSummaryReport({ report: requestResult.report, requestParams })
            } else {
              this.report = await this.marshallReport(requestResult.report)

              if (!this.report) {
                this.reportError = 'Based on the options, no unpaid revenue was found to generate a report'
              }
            }
          } else if (requestResult && this.isReportProcessingStatus(requestResult.status) && requestResult.scheduledJobId) {
            this.scheduledJobId = requestResult.scheduledJobId
            this.pollStransitghaPayoutReportStatus({
              scheduledJobId: requestResult.scheduledJobId,
              requestParams
            })
            return
          } else {
            this.reportError = requestResult && requestResult.message
              ? requestResult.message
              : 'Error in payout report generation'
          }
        } else {
          const fields = [
            // { key: '"Date from"', value: this.dateFrom },
            { key: '"Agency ID"', value: this.agencyId },
            { key: '"Money account"', value: this.moneyAccountId }]
            .concat(this.isStransitghaAgency() && !this.dateFrom && !this.dateTo ? [{ key: '"Date"', value: null }] : [])
            .filter(o => !o.value)
            .map(o => o.key).join(', ')

          addAlert({
            message: `The following required field(s) are missing: ${fields}`,
            type: 'error'
          })
        }
      } catch (err) {
        this.reportError = `Error in payout report: ${err && err.error ? err.error.message : (err && err.message ? err.message : JSON.stringify(err))}`
      } finally {
        if (!this.scheduledJobId) {
          this.apiInProgress = false
        }
      }
    },
    onFilterByAgency (val) {
      this.selectedAgency = val
    }
  },
  beforeDestroy () {
    this.clearStatusPollTimeout()

    if (this.$router.currentRoute.name !== 'transportation-accounts-payouts-payout') {
      if (this.lastParams && Object.keys(this.lastParams).length) {
        addParamsToLocation({ dateFrom: null, dateTo: null, agencyId: null, moneyAccountId: null }, this.$route, this.lastParams)
      }
    }
  }
}
